import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Button,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CallListParams } from 'retell-sdk/resources';
import { useSearchParams as useRouterSearchParams } from 'react-router-dom';

import {
  CollectWiseRetellPhoneCall,
  CollectWiseSearchParams,
  RangeFilter,
  listRetellCalls,
} from '../../api/hooks/calls/getCallList';
import LoadingSpinner from '../../components/LoadingSpinner';
import RetellCallComponent from './RetellCallComponent';
import RetellCallHistoryFiltersComponent from './RetellCallHistoryFiltersComponent';
import RetellCallCreditorNameSearch from './RetellCallCreditorNameSearch';

function readUrlParams(urlParams: URLSearchParams) {
  const rawPage = parseInt(urlParams.get('page') || '1', 10);
  const page = Math.max(0, (isNaN(rawPage) ? 1 : rawPage) - 1);
  const name = urlParams.get('name') || undefined;
  const clientId = urlParams.get('clientId') || undefined;
  const fileNumber = urlParams.get('fileNumber') || undefined;
  const toNumber = urlParams.get('toNumber') || undefined;
  const fromNumber = urlParams.get('fromNumber') || undefined;
  const startFrom = urlParams.get('startFrom') || undefined;
  const startTo = urlParams.get('startTo') || undefined;

  const filterCriteria: CallListParams.FilterCriteria & {
    clientId?: string;
    fileNumber?: string;
  } = {};

  if (toNumber) filterCriteria.to_number = [toNumber];
  if (fromNumber) filterCriteria.from_number = [fromNumber];
  if (clientId) filterCriteria.clientId = clientId;
  if (fileNumber) filterCriteria.fileNumber = fileNumber;

  if (startFrom || startTo) {
    const range: RangeFilter = {};
    if (startFrom) range.lower_threshold = parseInt(startFrom, 10);
    if (startTo) range.upper_threshold = parseInt(startTo, 10);
    filterCriteria.start_timestamp = range;
  }

  return { page, name, clientId, filterCriteria };
}

const RetellCallHistoryComponent: React.FC = () => {
  const [urlParams, setUrlParams] = useRouterSearchParams();
  const initialUrlState = readUrlParams(urlParams);

  const [page, setPage] = useState(0);
  const rowsPerPage = 25;
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  const [paginationHistory, setPaginationHistory] = useState<
    Record<number, string>
  >({});

  const [lastDocumentId, setLastDocumentId] = useState<string | null>(null);

  const [filterCriteria, setFilterCriteria] = useState<
    CallListParams.FilterCriteria & { clientId?: string; fileNumber?: string }
  >(initialUrlState.filterCriteria);

  const [searchParams, setSearchParams] = useState<{
    clientId?: string;
    name?: string;
  }>({
    name: initialUrlState.name,
    clientId: initialUrlState.clientId,
  });

  const {
    data: pageData,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
    failureCount,
  } = useQuery({
    queryKey: [
      'retellCalls',
      filterCriteria,
      page,
      searchParams.name,
      searchParams.clientId,
    ],
    queryFn: () => {
      const params: CollectWiseSearchParams = {
        pagination_key: paginationHistory[page],
        limit: rowsPerPage,
        lastDocumentId: lastDocumentId ?? undefined,
        filter_criteria:
          Object.keys(filterCriteria).length > 0 ? filterCriteria : undefined,
      };
      if (searchParams.name) params.name = searchParams.name;
      if (searchParams.clientId) params.clientId = searchParams.clientId;
      return listRetellCalls(params);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: (failureCount) => {
      return failureCount < MAX_RETRIES;
    },
  });

  useEffect(() => {
    const params = new URLSearchParams();

    if (page > 0) params.set('page', String(page + 1));
    if (searchParams.name) params.set('name', searchParams.name);
    if (searchParams.clientId) params.set('clientId', searchParams.clientId);

    if (filterCriteria.fileNumber)
      params.set('fileNumber', filterCriteria.fileNumber);
    if (filterCriteria.to_number?.[0])
      params.set('toNumber', filterCriteria.to_number[0]);
    if (filterCriteria.from_number?.[0])
      params.set('fromNumber', filterCriteria.from_number[0]);

    const startTimestamp = filterCriteria.start_timestamp as
      | RangeFilter
      | undefined;
    if (startTimestamp?.lower_threshold)
      params.set('startFrom', String(startTimestamp.lower_threshold));
    if (startTimestamp?.upper_threshold)
      params.set('startTo', String(startTimestamp.upper_threshold));

    setUrlParams(params, { replace: true });
  }, [page, searchParams, filterCriteria, setUrlParams]);

  useEffect(() => {
    if (failureCount > 0) {
      setRetryCount(failureCount);
    }
  }, [failureCount]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    if (newPage > page) {
      if (pageData && pageData?.lastDocumentId) {
        setPaginationHistory((prev) => ({
          ...prev,
          [newPage]: pageData.lastDocumentId as string,
        }));
      }
      setLastDocumentId(pageData?.lastDocumentId || null);
    } else if (newPage < page) {
      setLastDocumentId(paginationHistory[newPage] || null);
    }

    setPage(newPage);
  };

  const resetPagination = useCallback(() => {
    setPage(0);
    setPaginationHistory({});
  }, []);

  const handleRefresh = () => {
    setRetryCount(0); // Reset retry count on manual refresh
    refetch();
  };

  const handleSearchSubmit = useCallback(
    (searchTerm: string) => {
      setSearchParams((prev) => ({
        ...prev,
        name: searchTerm,
      }));

      resetPagination();
    },
    [resetPagination]
  );

  if (isLoading && !isFetching) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '4px',
          mb: 2,
        }}
      >
        <RetellCallCreditorNameSearch
          onSubmit={handleSearchSubmit}
          isLoading={isFetching}
          placeholder="Search by creditor name..."
          initialValue={initialUrlState.name || ''}
        />
        <Stack direction={'row'} spacing={2}>
          <RetellCallHistoryFiltersComponent
            filterCriteria={filterCriteria}
            setFilterCriteria={setFilterCriteria}
            resetPagination={resetPagination}
          />
          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} elevation={3}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell>
              <TableCell>Call Duration</TableCell>
              <TableCell>Debtor Name</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>File Number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Call ID</TableCell>
              <TableCell>To</TableCell>
              <TableCell>From</TableCell>
              <TableCell>Download Recording</TableCell>
              <TableCell>Disconnection Reason</TableCell>
              <TableCell>User Sentiment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                  <LoadingSpinner />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading call data...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : pageData?.calls?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                  <Typography>No call history found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              pageData?.calls?.map((call: CollectWiseRetellPhoneCall) => (
                <RetellCallComponent key={call.call_id} call={call} />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isError && (
        <Box sx={{ p: 3, mt: 2 }}>
          <Typography color="error">
            Error loading call history: {(error as Error).message}
            {retryCount > 0 && ` (Retry ${retryCount}/${MAX_RETRIES})`}
          </Typography>
          <Button
            variant="contained"
            onClick={handleRefresh}
            sx={{ mt: 2 }}
            disabled={isFetching}
          >
            Try Again
          </Button>
        </Box>
      )}

      <TablePagination
        component={'div'}
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
        count={-1}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25]}
        labelDisplayedRows={({ page }) => `Page ${page + 1}`}
        slotProps={{
          actions: {
            previousButton: {
              disabled: page === 0 || isFetching,
            },
            nextButton: {
              disabled: !pageData?.hasMore || isFetching,
            },
          },
        }}
      />
    </Box>
  );
};

export default RetellCallHistoryComponent;
